<template>
  <div class="recurring-dates-editor">
    <label
      v-if="!showsPeriodLabels"
      class="form-label recurring-dates-editor__label"
    >
      <FontAwesomeIcon icon="calendar-check" class="me-1" />
      {{ $t("production_strategy.recurring_execution_dates") }}
    </label>

    <div v-if="model.length > 0" class="recurring-dates-editor__list">
      <div
        v-for="(dateSpec, index) in model"
        :key="index"
        class="recurring-dates-editor__row"
      >
        <!-- Period Label for Half-Year and Quarterly -->
        <div
          v-if="
            executionTiming === 'HALF_YEAR' || executionTiming === 'QUARTERLY'
          "
          class="recurring-dates-editor__period-label"
        >
          <FontAwesomeIcon icon="calendar-check" class="me-1" />
          {{ getPeriodLabel(index) }}
        </div>
        <div class="recurring-dates-editor__fields">
          <!-- Month picker (hidden for MONTHLY timing) -->
          <div
            v-if="executionTiming !== 'MONTHLY'"
            class="recurring-dates-editor__field"
          >
            <select
              class="form-select"
              :value="dateSpec.month"
              @change="
                updateDateSpec(
                  index,
                  'month',
                  parseInt(($event.target as HTMLSelectElement).value)
                )
              "
              @blur="$emit('field-touched')"
              :class="{ 'is-invalid': hasValidationError }"
              :aria-label="$t('common.month')"
            >
              <option value="">{{ $t("common.select_month") }}</option>
              <option
                v-for="month in getAvailableMonths(index)"
                :key="month"
                :value="month"
              >
                {{ getMonthName(month) }}
              </option>
            </select>
          </div>
          <div class="recurring-dates-editor__field">
            <select
              class="form-select"
              :value="dateSpec.day"
              @change="
                updateDateSpec(
                  index,
                  'day',
                  parseInt(($event.target as HTMLSelectElement).value)
                )
              "
              @blur="$emit('field-touched')"
              :class="{ 'is-invalid': hasValidationError }"
              :aria-label="
                executionTiming === 'MONTHLY'
                  ? $t('production_strategy.monthly_delivery_day')
                  : $t('common.day')
              "
            >
              <option
                v-for="day in getAvailableDays(dateSpec.month)"
                :key="day.value"
                :value="day.value"
              >
                {{ day.label }}
              </option>
            </select>
          </div>
          <button
            v-if="executionTiming === 'FIXED_DATES'"
            type="button"
            class="btn recurring-dates-editor__remove"
            @click="removeDateSpec(index)"
            :title="$t('production_strategy.remove_recurring_date')"
          >
            <FontAwesomeIcon icon="trash" />
          </button>
        </div>
      </div>
    </div>

    <p class="recurring-dates-editor__helper">
      {{ helperText }}
    </p>

    <!-- Only show add button for FIXED_DATES -->
    <button
      v-if="executionTiming === 'FIXED_DATES'"
      type="button"
      class="btn btn-outline-primary btn-sm mt-2"
      @click="addDateSpec"
    >
      <FontAwesomeIcon icon="plus-circle" class="me-1" />
      {{ $t("production_strategy.add_recurring_date") }}
    </button>

    <div v-if="hasValidationError" class="invalid-feedback d-block mt-2">
      <FontAwesomeIcon icon="exclamation-triangle" class="me-1" />
      {{ $t("validations.required") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getDaysInMonth,
  getMonthName,
} from "@/dashboard/production_strategy/overview/PPA/ppaDateUtils";
import type { RecurringDateSpec } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  executionTiming: string;
  hasValidationError?: boolean;
}>();

const model = defineModel<RecurringDateSpec[]>({ required: true });

defineEmits<{
  "field-touched": [];
}>();

const { t } = useI18n();

const showsPeriodLabels = computed(
  () =>
    props.executionTiming === "HALF_YEAR" ||
    props.executionTiming === "QUARTERLY"
);

const helperText = computed(() => {
  const mapping: Record<string, string> = {
    MONTHLY: "production_strategy.recurring_dates_description_monthly",
    QUARTERLY: "production_strategy.recurring_dates_description_quarterly",
    HALF_YEAR: "production_strategy.recurring_dates_description_half_year",
    YEARLY: "production_strategy.recurring_dates_description_yearly",
    FIXED_DATES: "production_strategy.recurring_dates_description_fixed_dates",
  };
  const key = mapping[props.executionTiming];
  return key ? t(key) : t("production_strategy.recurring_dates_description");
});

/**
 * Get the period label for half-year and quarterly delivery timing
 */
const getPeriodLabel = (index: number): string => {
  const { executionTiming } = props;

  if (executionTiming === "HALF_YEAR") {
    // H2 for first picker, H1 for second picker as per requirements
    return index === 0
      ? t("production_strategy.h2_production_delivery_date")
      : t("production_strategy.h1_production_delivery_date");
  }

  if (executionTiming === "QUARTERLY") {
    // Maintain legacy order: first picker shows Q4, then Q1, Q2, Q3
    const quarterLabels = [
      t("production_strategy.q4_production_execution_date"),
      t("production_strategy.q1_production_execution_date"),
      t("production_strategy.q2_production_execution_date"),
      t("production_strategy.q3_production_execution_date"),
    ];
    return quarterLabels[index] || quarterLabels[0];
  }

  return "";
};

/**
 * Get available days for a month, with special handling for monthly timing
 */
const getAvailableDays = (
  month: number
): Array<{ value: number; label: string }> => {
  const { executionTiming } = props;
  const year = new Date().getFullYear();
  const daysInMonth = getDaysInMonth(month, year);

  const days: Array<{ value: number; label: string }> = [];

  // Add regular days 1-28 (safe for all months)
  for (let day = 1; day <= Math.min(28, daysInMonth); day++) {
    days.push({ value: day, label: day.toString() });
  }

  // Add days 29-30 if valid for the month
  if (daysInMonth >= 29) {
    days.push({ value: 29, label: "29" });
  }
  if (daysInMonth >= 30) {
    days.push({ value: 30, label: "30" });
  }

  // For monthly timing, add "Last day" option (day=31)
  // For other timings, add day 31 only if it's valid for the month
  if (executionTiming === "MONTHLY") {
    days.push({ value: 31, label: t("production_strategy.last_day_of_month") });
  } else if (daysInMonth >= 31) {
    days.push({ value: 31, label: "31" });
  }

  return days;
};

/**
 * Get available months for a specific date index based on delivery timing
 */
const getAvailableMonths = (index: number): number[] => {
  const { executionTiming } = props;

  if (executionTiming === "HALF_YEAR") {
    // First picker shows H1 months (Jan-Jun), Second picker shows H2 months (Jul-Dec)
    return index === 0 ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
  }

  if (executionTiming === "QUARTERLY") {
    // First picker Q1 (Jan-Mar), Second Q2 (Apr-Jun), Third Q3 (Jul-Sep), Fourth Q4 (Oct-Dec)
    const quarterMonths = [
      [1, 2, 3], // Q1: Jan-Mar (first picker)
      [4, 5, 6], // Q2: Apr-Jun (second picker)
      [7, 8, 9], // Q3: Jul-Sep (third picker)
      [10, 11, 12], // Q4: Oct-Dec (fourth picker)
    ];
    return quarterMonths[index] || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  }

  // For YEARLY and FIXED_DATES, show all months
  // For MONTHLY, this will be hidden anyway
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
};

const addDateSpec = () => {
  // Only for FIXED_DATES - other timing types have fixed counts
  if (props.executionTiming !== "FIXED_DATES") return;

  // Find next available month
  const usedMonths = new Set(model.value.map((spec) => spec.month));
  let nextMonth = 1;

  // Find first unused month, or wrap around to next month after highest used
  for (let month = 1; month <= 12; month++) {
    if (!usedMonths.has(month)) {
      nextMonth = month;
      break;
    }
  }

  // If all months are used, use month after the highest used month (wrapping to 1 if needed)
  if (usedMonths.size === 12) {
    const maxUsedMonth = Math.max(...model.value.map((spec) => spec.month));
    nextMonth = maxUsedMonth === 12 ? 1 : maxUsedMonth + 1;
  }

  model.value = [...model.value, { day: 1, month: nextMonth }];
};

const updateDateSpec = (
  index: number,
  field: "day" | "month",
  value: number
) => {
  if (isNaN(value)) return;

  const updated = [...model.value];
  updated[index] = {
    ...updated[index],
    [field]: value,
  };
  model.value = updated;
};

const removeDateSpec = (index: number) => {
  if (props.executionTiming !== "FIXED_DATES") return;

  model.value = model.value.filter((_, i) => i !== index);
};
</script>

<style scoped lang="scss">
.recurring-dates-editor__label {
  display: inline-flex;
  align-items: center;
}

.recurring-dates-editor__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 4px;
}

.recurring-dates-editor__row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.recurring-dates-editor__period-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-gray-700);

  :deep(svg) {
    color: var(--phoenix-gray-400);
  }
}

.recurring-dates-editor__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: center;
}

.recurring-dates-editor__fields:has(.recurring-dates-editor__remove) {
  grid-template-columns: 1fr 1fr auto;
}

.recurring-dates-editor__fields > .recurring-dates-editor__field:only-child,
.recurring-dates-editor__field:only-child {
  grid-column: 1 / -1;
}

.recurring-dates-editor__helper {
  margin: 0;
  font-size: 12px;
  color: var(--phoenix-gray-500);
  line-height: 1.45;
}

.recurring-dates-editor__remove {
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--phoenix-gray-500);
  background: transparent;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 8px;

  &:hover {
    color: var(--phoenix-danger);
    border-color: var(--phoenix-danger-border-subtle);
    background: var(--phoenix-danger-bg-subtle);
  }
}
</style>
